import React, { useRef, useState, useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Home from '../components/Home';
import Hero from '../components/Hero';
import styled from 'styled-components';
import NewsCard from '../components/NewsCard';
import ReactMarkdown from 'react-markdown';
import { Seo } from '../components/SEO';

const HomePage = styled.div`
  position: relative;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const NewsCarouselContainer = styled.div`
  position: relative;
  padding-left: 5rem;
  padding-right: 180px;
  margin: 30px 0 5px 0;
  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 100px;
  }
`;

const NewsCarousel = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 20px;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    flex: 0 0 auto;
    margin-right: 16px;
    scroll-snap-align: start;

    &:last-child {
      margin-right: 5rem;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 999;
  width: ${(props) => (props.$isMobile ? '100%' : '500px')};
  height: 100%;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  -webkit-overflow-scrolling: touch;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContent = styled.div`
  padding: ${(props) => (props.$isMobile ? '20px' : '40px')};
  height: 100%;
  overflow-y: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  background: white;

  @media (max-width: 768px) {
    padding-bottom: 60px;
    min-height: calc(100% + 1px);
    -webkit-transform: translateZ(0);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #000;
  padding: 8px;
  z-index: 2;

  &:hover {
    background-color: #f5f5f5;
    border-radius: 50%;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  margin: 12px 0;
  border: 1px solid #000;
`;

const Date = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  font-weight: 400;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 500;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const Content = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${(props) => (props.$expanded ? 'unset' : '3')};

  p {
    margin: 0;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease;
  z-index: 99;
`;

const IndexPage = () => {
  const homePageRef = useRef(null);
  const newsCarouselRef = useRef(null);
  const [activeNews, setActiveNews] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setScrollPosition(window.pageYOffset);
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.position = 'fixed';
      document.documentElement.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollPosition);
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.overflow = '';
    };
  }, [isModalOpen, scrollPosition]);

  const data = useStaticQuery(graphql`
    query {
      allMdx(
        filter: { frontmatter: { type: { eq: "news" } } }
        sort: { frontmatter: { date: DESC } }
      ) {
        nodes {
          id
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            content
            images {
              image
            }
          }
        }
      }
    }
  `);

  const news = data.allMdx.nodes;

  const handleOpenModal = (news) => {
    setActiveNews(news);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveNews(null);
  };

  return (
    <>
      <HomePage ref={homePageRef}>
        <Hero />
        <Home />
        {news.length > 0 && (
          <NewsCarouselContainer>
            <NewsCarousel ref={newsCarouselRef}>
              {news.map((newsItem) => (
                <NewsCard
                  key={newsItem.id}
                  news={{
                    title: newsItem.frontmatter.title,
                    date: newsItem.frontmatter.date,
                    content: newsItem.frontmatter.content,
                    images: newsItem.frontmatter.images,
                  }}
                  onOpenModal={handleOpenModal}
                />
              ))}
            </NewsCarousel>
          </NewsCarouselContainer>
        )}
      </HomePage>

      <Overlay $isOpen={isModalOpen} onClick={handleCloseModal} />
      <Modal $isOpen={isModalOpen} $isMobile={isMobile}>
        {activeNews && (
          <ModalContent
            $isMobile={isMobile}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={handleCloseModal}>&times;</CloseButton>
            <Date>{activeNews.date}</Date>
            <Title>{activeNews.title}</Title>
            {activeNews.images?.length > 0 && (
              <div>
                {activeNews.images.map((img, index) => (
                  <ModalImage
                    key={index}
                    src={img.image}
                    alt={`News image ${index + 1}`}
                  />
                ))}
              </div>
            )}
            <Content $expanded>
              <ReactMarkdown>{activeNews.content}</ReactMarkdown>
            </Content>
          </ModalContent>
        )}
      </Modal>
    </>
  );
};

export default IndexPage;

export const Head = () => (
  <Seo
    title="Jesse Gao - HCI Researcher & Designer"
    description="Jesse Gao is a PhD student at Northwestern University interested in Human Digitization, with expertise in UX design, XR, and interactive systems."
    pathname="/"
  />
);
